import { useCallback, useEffect, useState } from 'react';
import { postJson } from '../services/http';

/**
 * PWA install state for the public /about page.
 *
 * `beforeinstallprompt` can fire before the page mounts, so index.js stashes
 * it on window.__onscreenInstallPrompt and this hook picks it up from there.
 *
 * `mode` drives which install UI to show:
 *   installed      – already running standalone; offer "Open"
 *   prompt         – Chromium gave us a native one-tap prompt
 *   ios            – Safari / iOS browsers: Share → Add to Home Screen
 *   android-manual – Android without a prompt (Samsung, Firefox…): browser menu
 *   mac-safari     – Safari 17+ on macOS: File → Add to Dock
 *   desktop-manual – desktop Chromium that hasn't fired a prompt (yet)
 *   inapp          – Instagram/Facebook/TikTok webviews, which can't install
 *   unsupported    – e.g. desktop Firefox; fall back to the browser
 */

const INAPP_RE = /FBAN|FBAV|FB_IAB|Instagram|Line\/|TikTok|musical_ly|BytedanceWebview|Snapchat|Pinterest|LinkedInApp|Twitter/i;

export const detectInstallPlatform = (nav = window.navigator) => {
  const ua = nav.userAgent || '';
  const isIPadOS = /Macintosh/.test(ua) && nav.maxTouchPoints > 1;
  const ios = /iPad|iPhone|iPod/.test(ua) || isIPadOS;
  const android = /Android/i.test(ua);
  return {
    ios,
    iphone: /iPhone|iPod/.test(ua),
    android,
    inapp: INAPP_RE.test(ua),
    mobile: ios || android || /Mobi/i.test(ua),
    chromium: /Chrome|Chromium|CriOS|Edg\//.test(ua) && !/OPR\/|SamsungBrowser/.test(ua),
    macSafari: !ios && /Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome|Chromium|Edg\/|Firefox/.test(ua),
    firefox: /Firefox|FxiOS/.test(ua),
  };
};

export const isRunningStandalone = () =>
  (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) ||
  window.navigator.standalone === true;

export const resolveInstallMode = ({ installed, hasPrompt, platform }) => {
  if (installed) return 'installed';
  if (platform.inapp) return 'inapp';
  if (hasPrompt) return 'prompt';
  if (platform.ios) return 'ios';
  if (platform.android) return 'android-manual';
  if (platform.macSafari) return 'mac-safari';
  if (platform.chromium && !platform.mobile) return 'desktop-manual';
  return 'unsupported';
};

// Install-funnel beacon. Reuses the anonymous site-visit log with an
// allowlisted "/about#<event>" path (see TRACKED_SITE_PATHS server-side).
export const trackInstallEvent = (event) => {
  postJson('/api/tracking/site-visit', { path: `/about#${event}` }).catch(() => {});
};

export default function useInstallPrompt() {
  const [platform] = useState(() => detectInstallPlatform());
  const [deferredPrompt, setDeferredPrompt] = useState(() => window.__onscreenInstallPrompt || null);
  const [installed, setInstalled] = useState(() => isRunningStandalone());

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      window.__onscreenInstallPrompt = e;
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      window.__onscreenInstallPrompt = null;
      setDeferredPrompt(null);
      setInstalled(true);
      trackInstallEvent('installed');
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // Returns 'accepted' | 'dismissed' | 'unavailable'.
  const install = useCallback(async () => {
    if (!deferredPrompt) return 'unavailable';
    trackInstallEvent('install-click');
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      trackInstallEvent(outcome === 'accepted' ? 'install-accepted' : 'install-dismissed');
      if (outcome === 'accepted') setInstalled(true);
      return outcome;
    } catch (err) {
      console.error('PWA install prompt failed:', err);
      return 'unavailable';
    } finally {
      // A prompt event can only be used once.
      window.__onscreenInstallPrompt = null;
      setDeferredPrompt(null);
    }
  }, [deferredPrompt]);

  return {
    platform,
    installed,
    mode: resolveInstallMode({ installed, hasPrompt: !!deferredPrompt, platform }),
    install,
  };
}
